import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, Award, ArrowRight } from 'lucide-react';
import axios from 'axios';
import './LevelSystem.css';

const LevelProgress = ({ user, showDetails = true, compact = false, t }) => {
  const [levelData, setLevelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (user) {
      fetchLevelData();
    }
  }, [user]);

  const fetchLevelData = async () => {
    try {
      const response = await axios.get('/api/levels/my-level');
      setLevelData(response.data);
      setError(false);
    } catch (error) {
      console.error('Failed to fetch level data:', error);
      setError(true);
      // Enhanced fallback to basic level data
      const calculatedLevel = getUserLevel();
      const totalExp = user?.totalExp || user?.exp || 0;
      const progressData = calculateLevelProgress(totalExp, calculatedLevel);
      
      setLevelData({
        level: calculatedLevel,
        title: getUserLevelTitle(),
        totalExp: totalExp,
        currentLevelExp: progressData.currentLevelExp,
        expNeededForNext: progressData.expNeededForNext,
        expForNextLevel: progressData.expForNextLevel,
        progressPercentage: progressData.progressPercentage,
        isMaxLevel: calculatedLevel >= 100,
        nextMilestones: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Get user level from available data
  const getUserLevel = () => {
    // Debug in development
    if (process.env.NODE_ENV === 'development') {
      console.log('LevelProgress - User fields:', {
        level: user?.level,
        currentLevel: user?.currentLevel,
        totalExp: user?.totalExp,
        exp: user?.exp,
        allFields: user ? Object.keys(user) : []
      });
    }

    // Try different possible field names
    if (user?.currentLevel) return user.currentLevel;
    if (user?.level) return user.level;
    
    // Calculate from total EXP if available
    if (user?.totalExp !== undefined) {
      return calculateLevelFromExp(user.totalExp);
    }
    
    // Calculate from exp field
    if (user?.exp !== undefined) {
      return calculateLevelFromExp(user.exp);
    }
    
    return 1; // Default fallback
  };

  const getUserLevelTitle = () => {
    if (user?.levelTitle) return user.levelTitle;
    if (user?.title) return user.title;
    return getLevelTitle(getUserLevel());
  };

  // Simple level calculation based on EXP (matching server logic)
  const calculateLevelFromExp = (totalExp) => {
    if (!totalExp || totalExp < 0) return 1;
    
    // Level structure from server
    const levelStructure = [
      ...Array(5).fill(50),      // Levels 1-5: 50 EXP each
      ...Array(5).fill(75),      // Levels 6-10: 75 EXP each  
      ...Array(10).fill(100),    // Levels 11-20: 100 EXP each
      ...Array(10).fill(200),    // Levels 21-30: 200 EXP each
      ...Array(20).fill(300),    // Levels 31-50: 300 EXP each
      ...Array(30).fill(500),    // Levels 51-80: 500 EXP each
      ...Array(10).fill(1000),   // Levels 81-90: 1000 EXP each
      ...Array(10).fill(2000)    // Levels 91-100: 2000 EXP each
    ];
    
    let currentLevel = 1;
    let expUsed = 0;
    
    for (let level = 1; level <= 100; level++) {
      const expNeededForThisLevel = levelStructure[level - 1] || 0;
      
      if (expUsed + expNeededForThisLevel <= totalExp) {
        expUsed += expNeededForThisLevel;
        currentLevel = level + 1;
      } else {
        break;
      }
    }
    
    return Math.min(currentLevel, 100);
  };

  // Calculate level progress details
  const calculateLevelProgress = (totalExp, currentLevel) => {
    const levelStructure = [
      ...Array(5).fill(50), ...Array(5).fill(75), ...Array(10).fill(100),
      ...Array(10).fill(200), ...Array(20).fill(300), ...Array(30).fill(500),
      ...Array(10).fill(1000), ...Array(10).fill(2000)
    ];
    
    let expForCurrentLevel = 0;
    for (let i = 0; i < currentLevel - 1; i++) {
      expForCurrentLevel += levelStructure[i] || 0;
    }
    
    const expForNextLevel = levelStructure[currentLevel - 1] || 0;
    const currentLevelExp = totalExp - expForCurrentLevel;
    const expNeededForNext = Math.max(0, expForNextLevel - currentLevelExp);
    const progressPercentage = expForNextLevel > 0 ? (currentLevelExp / expForNextLevel) * 100 : 0;
    
    return {
      currentLevelExp: Math.max(0, currentLevelExp),
      expForNextLevel,
      expNeededForNext,
      progressPercentage: Math.min(100, Math.max(0, progressPercentage))
    };
  };

  // Basic level title calculation for fallback
  const getLevelTitle = (level) => {
    if (level >= 100) return 'Legend';
    if (level >= 90) return 'Sage';
    if (level >= 80) return 'Mythic';
    if (level >= 70) return 'Grandmaster';
    if (level >= 60) return 'Master';
    if (level >= 50) return 'Expert';
    if (level >= 40) return 'Professional';
    if (level >= 30) return 'Advanced';
    if (level >= 20) return 'Intermediate';
    if (level >= 10) return 'Novice';
    return 'Beginner';
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="level-progress">
        <div className="level-progress-header">
          <div className="level-progress-title">
            <TrendingUp className="w-4 h-4" />
            <span>{t ? t('loadingProgress', 'Loading progress...') : 'Loading progress...'}</span>
          </div>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar" style={{ width: '0%' }}></div>
        </div>
      </div>
    );
  }

  // Safety check for levelData with enhanced fallback
  if (!levelData) {
    const fallbackLevel = getUserLevel();
    const fallbackTitle = getUserLevelTitle();
    const totalExp = user?.totalExp || user?.exp || 0;
    const progressData = calculateLevelProgress(totalExp, fallbackLevel);
    
    return (
      <div className="level-progress">
        <div className="level-progress-header">
          <div className="level-progress-title">
            <TrendingUp className="w-4 h-4" />
            <span>{t ? t('levelProgress', 'Level Progress') : 'Level Progress'}</span>
          </div>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar" style={{ width: `${progressData.progressPercentage}%` }}></div>
        </div>
        <div className="progress-details">
          <span>Level {fallbackLevel} - {fallbackTitle}</span>
          <span>{Math.round(progressData.progressPercentage)}%</span>
        </div>
      </div>
    );
  }

  const level = levelData.level || getUserLevel();
  const title = levelData.title || getUserLevelTitle();
  const currentLevelExp = levelData.currentLevelExp || 0;
  const expForNextLevel = levelData.expForNextLevel || 50;
  const expNeededForNext = levelData.expNeededForNext || 50;
  const progressPercentage = levelData.progressPercentage || 0;
  const isMaxLevel = levelData.isMaxLevel || level >= 100;
  const nextMilestones = levelData.nextMilestones || [];

  if (compact) {
    return (
      <div className="level-progress-compact">
        <div className="progress-bar-container">
          <div 
            className="progress-bar" 
            style={{ width: `${Math.min(100, progressPercentage)}%` }}
          ></div>
        </div>
        <div className="progress-details">
          <span>{formatNumber(currentLevelExp)}</span>
          <span>{formatNumber(expForNextLevel)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="level-progress">
      <div className="level-progress-header">
        <div className="level-progress-title">
          <TrendingUp className="w-4 h-4" />
          <span>{t ? t('levelProgress', 'Level Progress') : 'Level Progress'}</span>
          {error && (
            <span style={{ color: '#ef4444', fontSize: '12px', marginLeft: '8px' }}>
              ({t ? t('offline', 'Offline') : 'Offline'})
            </span>
          )}
        </div>
        <div className="level-progress-stats">
          {t ? t('level', 'Level') : 'Level'} {level} - {title}
        </div>
      </div>

      <div className="progress-bar-container">
        <div 
          className="progress-bar" 
          style={{ width: `${Math.min(100, progressPercentage)}%` }}
        ></div>
      </div>

      <div className="progress-details">
        <span>
          {formatNumber(currentLevelExp)} / {formatNumber(expForNextLevel)} EXP
        </span>
        <span>
          {isMaxLevel ? 
            (t ? t('maxLevel', 'MAX LEVEL!') : 'MAX LEVEL!') : 
            `${Math.round(progressPercentage)}%`
          }
        </span>
      </div>

      {showDetails && !isMaxLevel && (
        <div className="next-level-info">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Target className="w-3 h-3" />
            <span>{t ? t('nextLevel', 'Next Level') : 'Next Level'}: {level + 1}</span>
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
            {formatNumber(expNeededForNext)} {t ? t('expNeeded', 'EXP needed') : 'EXP needed'}
          </div>
          
          {nextMilestones.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <Award className="w-3 h-3" />
                <span style={{ fontSize: '11px', fontWeight: '600' }}>
                  {t ? t('upcomingMilestones', 'Upcoming Milestones') : 'Upcoming Milestones'}
                </span>
              </div>
              {nextMilestones.slice(0, 2).map((milestone, index) => (
                <div key={milestone.level || index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  fontSize: '10px',
                  color: '#d1d5db',
                  marginBottom: '4px'
                }}>
                  <span>
                    {t ? t('level', 'Level') : 'Level'} {milestone.level}: {milestone.title}
                  </span>
                  <span style={{ color: '#9ca3af' }}>
                    {formatNumber(milestone.expNeeded)} EXP
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showDetails && isMaxLevel && (
        <div className="next-level-info">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '8px',
            color: '#fbbf24',
            fontWeight: '600'
          }}>
            <Award className="w-4 h-4" />
            <span>{t ? t('congratsMaxLevel', 'Congratulations! You\'ve reached the maximum level!') : 'Congratulations! You\'ve reached the maximum level!'}</span>
          </div>
          <div style={{ 
            textAlign: 'center', 
            fontSize: '11px', 
            color: '#d1d5db',
            marginTop: '6px'
          }}>
            {t ? t('totalExpEarned', 'Total EXP earned') : 'Total EXP earned'}: {formatNumber(levelData.totalExp || 0)}
          </div>
        </div>
      )}
    </div>
  );
};

export default LevelProgress;